import React, { useState, useEffect } from 'react';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';

const ChooseSeats = ({
  events,
  activeEventIndex,
  setActiveEventIndex,
  onSeatSelected,
  onSeatDeselected,
  primaryColor,
  setShowSeats,
  selectedSlot,
}) => {
  const activeEvent = events[activeEventIndex];
  const [selectedEventSlot, setSelectedEventSlot] = useState(null);

  useEffect(() => {
    if (activeEvent) {
      const defaultSlot = (activeEvent.timeslots || []).find(
        ts => selectedSlot && ts.startTime === selectedSlot.startTime && ts.endTime === selectedSlot.endTime
      ) || activeEvent.timeslots?.[0] || activeEvent;
      setSelectedEventSlot(defaultSlot);
    } else {
      setSelectedEventSlot(null);
    }
  }, [activeEvent, selectedSlot]);

  if (!activeEvent) return null;

  const publicKey = selectedEventSlot?.seatPublicKey ? atob(selectedEventSlot.seatPublicKey) : null;
  const eventKey = selectedEventSlot?.seatEventId ? atob(selectedEventSlot.seatEventId) : (selectedEventSlot?.gid || activeEvent.gid);

  const formatTime = (minutes) => {
    if (minutes === undefined || minutes === null) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMins = mins.toString().padStart(2, '0');
    return `${displayHours}:${displayMins} ${ampm}`;
  };

  return (
    <div className="bh-next-section bh-next-seats-section" style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button
          style={{
            background: 'transparent',
            border: '1px solid #E5E7EB',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#4B5563'
          }}
          onClick={() => setShowSeats(false)}
        >
          &larr;
        </button>
        <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Choose Your Seats</h2>
      </div>

      {/* Event Tabs */}
      {events.length > 1 && (
        <div className="bh-event-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', borderBottom: '1px solid #E5E7EB', paddingBottom: '8px' }}>
          {events.map((evt, idx) => (
            <button
              key={evt.gid}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: activeEventIndex === idx ? primaryColor : 'transparent',
                color: activeEventIndex === idx ? '#fff' : '#4B5563',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}
              onClick={() => setActiveEventIndex(idx)}
            >
              {evt.name || `Event ${idx + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Event Timeslot Selector (if there are timeslots inside this event) */}
      {activeEvent.timeslots && activeEvent.timeslots.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#4B5563', display: 'block', marginBottom: '8px' }}>
            Select Seated Event Time Slot:
          </label>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
            {activeEvent.timeslots.map(slot => {
              const label = `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
              const isSelected = selectedEventSlot && selectedEventSlot.startTime === slot.startTime && selectedEventSlot.endTime === slot.endTime;
              return (
                <button
                  key={`${slot.startTime}-${slot.endTime}`}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid',
                    borderColor: isSelected ? primaryColor : '#E5E7EB',
                    background: isSelected ? `${primaryColor}12` : '#fff',
                    color: isSelected ? primaryColor : '#4B5563',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={() => setSelectedEventSlot(slot)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Name & Description */}
      {activeEvent && (
        <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b', marginBottom: '4px' }}>
            {activeEvent.name}
          </div>
          {activeEvent.description && (
            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
              {activeEvent.description}
            </div>
          )}
        </div>
      )}

      {/* Seats.io Seating Chart */}
      {!publicKey || !eventKey || !selectedEventSlot ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6B7280' }}>
          Seating configuration is missing for this event.
        </div>
      ) : (
        <div key={selectedEventSlot.gid || `${selectedEventSlot.startTime}-${selectedEventSlot.endTime}`} style={{ minHeight: '500px', width: '100%', background: '#F9FAFB', borderRadius: '8px', padding: '12px' }}>
          <SeatsioSeatingChart
            workspaceKey={publicKey}
            event={eventKey}
            showFullScreenButton
            region="na"
            onObjectSelected={(object) => onSeatSelected(object, selectedEventSlot)}
            onObjectDeselected={onSeatDeselected}
            holdOnSelect={true}
            multiSelect={true}
            confirmSelectionOnMobile={false}
            select={{
              mode: 'multi',
              autoSelectOneTicketType: true
            }}
            language="en"
          />
        </div>
      )}
    </div>
  );
};

export default ChooseSeats;
